import { Toggle } from '@/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSettingsStore } from '@/store/use-settings-store';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

const EST_FILL_PRICE_SPREAD: number = Number(
  import.meta.env.VITE_EST_FILL_PRICE_SPREAD_PCT || 1,
);
const EST_CLOSE_PRICE_SPREAD: number = Number(
  import.meta.env.VITE_EST_CLOSE_PRICE_SPREAD_PCT || 1,
);

export default function ChartSettings() {
  const { t } = useTranslation();
  const { showOrderMarks, showEstClosePrice, showEstFillPrice, showTpslLines } =
    useSettingsStore(
      useShallow((state) => ({
        showOrderMarks: state.showOrderMarks,
        showEstClosePrice: state.showEstClosePrice,
        showEstFillPrice: state.showEstFillPrice,
        showTpslLines: state.showTpslLines,
      })),
    );
  return (
    <div className="space-y-8 pb-32 sm:pb-10">
      <div className="flex items-center justify-between gap-6">
        <div className="space-y-1">
          <span>{t('settingsDropdown.estFillPrice')}</span>
          <p className="text-muted-foreground">
            {t('settingsDropdown.estFillPriceTooltip', {
              gap: EST_FILL_PRICE_SPREAD,
            })}
          </p>
        </div>
        <Select
          value={
            showEstFillPrice === null
              ? 'auto'
              : showEstFillPrice
                ? 'true'
                : 'false'
          }
          onValueChange={(v) =>
            useSettingsStore.setState({
              showEstFillPrice:
                v === 'true' ? true : v === 'false' ? false : null,
            })
          }
        >
          <SelectTrigger
            id="estFillPriceFilter"
            className="max-w-24 rounded-none bg-muted text-base"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            <SelectItem className="text-base" value="auto">
              {t('settingsDropdown.auto')}
            </SelectItem>
            <SelectItem className="text-base" value="true">
              {t('settingsDropdown.on')}
            </SelectItem>
            <SelectItem className="text-base" value="false">
              {t('settingsDropdown.off')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between gap-6">
        <div className="space-y-1">
          <span>{t('settingsDropdown.estClosePrice')}</span>
          <p className="text-muted-foreground">
            {t('settingsDropdown.estClosePriceTooltip', {
              gap: EST_CLOSE_PRICE_SPREAD,
            })}
          </p>
        </div>
        <Select
          value={
            showEstClosePrice === null
              ? 'auto'
              : showEstClosePrice
                ? 'true'
                : 'false'
          }
          onValueChange={(v) =>
            useSettingsStore.setState({
              showEstClosePrice:
                v === 'true' ? true : v === 'false' ? false : null,
            })
          }
        >
          <SelectTrigger
            id="estClosePriceFilter"
            className="max-w-24 rounded-none bg-muted text-base"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            <SelectItem className="text-base" value="auto">
              {t('settingsDropdown.auto')}
            </SelectItem>
            <SelectItem className="text-base" value="true">
              {t('settingsDropdown.on')}
            </SelectItem>
            <SelectItem className="text-base" value="false">
              {t('settingsDropdown.off')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between gap-6">
        <div className="space-y-1">
          <span>{t('settingsDropdown.tpslLines')}</span>
          <p className="text-muted-foreground">
            {t('settingsDropdown.tpslLinesTooltip')}
          </p>
        </div>
        <Toggle
          checked={showTpslLines}
          onCheckedChange={(checked) =>
            useSettingsStore.setState({ showTpslLines: checked })
          }
          aria-label={t('settingsDropdown.tpslLines')}
          className="data-[state=checked]:bg-primary"
        />
      </div>
      <div className="flex items-center justify-between gap-6">
        <div className="space-y-1">
          <span>{t('settingsDropdown.orderMarks')}</span>
          <p className="text-muted-foreground">
            {t('settingsDropdown.orderMarksTooltip')}
          </p>
        </div>
        <Toggle
          aria-label={t('settingsDropdown.orderMarks')}
          className="data-[state=checked]:bg-primary"
          checked={showOrderMarks}
          onCheckedChange={(checked) =>
            useSettingsStore.setState({ showOrderMarks: checked })
          }
        />
      </div>
    </div>
  );
}
